import { useState, useEffect, useCallback } from "react";
import {
  getReportSettings,
  updateReportSettings,
  saveProviderBudget as saveProviderBudgetApi,
} from "../services/analyticsService";

/**
 * Fetches and updates report settings (annual budget and optional footprint baseline targets by year).
 * @param {number|string|null} [tenantId] - Required for Super Admin; tenant users use JWT tenant.
 * @returns {Object} { annualBudgetByYear, footprintBaselineByYear, providerBudgetByYear, loading, error, refresh, updateBudget, updateFootprintBaseline, saveProviderBudget }
 */
export const useReportSettings = (tenantId) => {
  const [annualBudgetByYear, setAnnualBudgetByYear] = useState({});
  const [footprintBaselineByYear, setFootprintBaselineByYear] = useState({});
  const [providerBudgetByYear, setProviderBudgetByYear] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getReportSettings(tenantId);
      if (response?.success && response?.data) {
        setAnnualBudgetByYear(response.data.annualBudgetByYear || {});
        setFootprintBaselineByYear(response.data.footprintBaselineByYear || {});
        setProviderBudgetByYear(response.data.providerBudgetByYear || {});
      } else {
        setAnnualBudgetByYear({});
        setFootprintBaselineByYear({});
        setProviderBudgetByYear({});
      }
    } catch (err) {
      setError(err);
      setAnnualBudgetByYear({});
      setFootprintBaselineByYear({});
      setProviderBudgetByYear({});
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateBudget = useCallback(async (newAnnualBudgetByYear) => {
    try {
      const response = await updateReportSettings(
        { annualBudgetByYear: newAnnualBudgetByYear },
        tenantId,
      );
      if (response?.success && response?.data) {
        setAnnualBudgetByYear(response.data.annualBudgetByYear || {});
        if (response.data.footprintBaselineByYear) {
          setFootprintBaselineByYear(response.data.footprintBaselineByYear);
        }
        if (response.data.providerBudgetByYear) {
          setProviderBudgetByYear(response.data.providerBudgetByYear);
        }
        return { success: true };
      }
      return { success: false, error: response?.message };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [tenantId]);

  const updateFootprintBaseline = useCallback(async (footprintBaselineByYearPatch) => {
    try {
      const response = await updateReportSettings(
        { footprintBaselineByYear: footprintBaselineByYearPatch },
        tenantId,
      );
      if (response?.success && response?.data) {
        setAnnualBudgetByYear(response.data.annualBudgetByYear || {});
        setFootprintBaselineByYear(response.data.footprintBaselineByYear || {});
        if (response.data.providerBudgetByYear) {
          setProviderBudgetByYear(response.data.providerBudgetByYear);
        }
        return { success: true };
      }
      return { success: false, error: response?.message };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [tenantId]);

  const saveProviderBudget = useCallback(async (payload) => {
    try {
      const response = await saveProviderBudgetApi(payload, tenantId);

      if (response?.success) {
        await fetchData();
        return {
          success: true,
          data: response.data,
          annual_budget: response.annual_budget,
          entryType: response.entryType,
          granularity: response.granularity,
        };
      }

      return { success: false, error: response?.message };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [fetchData, tenantId]);

  return {
    annualBudgetByYear,
    footprintBaselineByYear,
    providerBudgetByYear,
    loading,
    error,
    refresh: fetchData,
    updateBudget,
    updateFootprintBaseline,
    saveProviderBudget,
  };
};
